import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  Edit2,
  Eye,
  FileText,
  Plus,
  Search,
  Wrench,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, Money, StatusPill } from "@/components/ui";
import {
  createCustomer,
  getCustomerDetail,
  listCustomers,
  updateCustomer,
  type CustomerCreate,
  type CustomerPublic,
  type CustomerUpdate,
} from "@/lib/customers";

export function CustomersScreen() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<CustomerPublic | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    phone: string;
    email: string;
    tax_id: string;
    legal_name: string;
    tax_regime: string;
    zip_code: string;
    credit_limit_pesos: string;
    credit_days: string;
    notes: string;
  }>({
    name: "",
    phone: "",
    email: "",
    tax_id: "",
    legal_name: "",
    tax_regime: "601",
    zip_code: "",
    credit_limit_pesos: "0",
    credit_days: "0",
    notes: "",
  });

  const { data: customersData, isLoading } = useQuery({
    queryKey: ["customers", searchTerm],
    queryFn: () => listCustomers({ q: searchTerm || undefined }),
  });

  const { data: detailData, isLoading: isLoadingDetail } = useQuery({
    queryKey: ["customers", "detail", selectedCustomerId],
    queryFn: () => getCustomerDetail(selectedCustomerId!),
    enabled: Boolean(selectedCustomerId),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CustomerCreate) => createCustomer(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setShowCreateModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CustomerUpdate }) =>
      updateCustomer(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setEditingCustomer(null);
      resetForm();
    },
  });

  function resetForm() {
    setFormData({
      name: "",
      phone: "",
      email: "",
      tax_id: "",
      legal_name: "",
      tax_regime: "601",
      zip_code: "",
      credit_limit_pesos: "0",
      credit_days: "0",
      notes: "",
    });
  }

  function handleOpenCreate() {
    resetForm();
    setShowCreateModal(true);
  }

  function handleOpenEdit(customer: CustomerPublic) {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone ?? "",
      email: customer.email ?? "",
      tax_id: customer.tax_id ?? "",
      legal_name: customer.legal_name ?? "",
      tax_regime: customer.tax_regime ?? "601",
      zip_code: customer.zip_code ?? "",
      credit_limit_pesos: String(customer.credit_limit_cents / 100),
      credit_days: String(customer.credit_days),
      notes: customer.notes ?? "",
    });
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const limitCents = Math.round(parseFloat(formData.credit_limit_pesos || "0") * 100);
    const days = parseInt(formData.credit_days || "0", 10);

    if (editingCustomer) {
      updateMutation.mutate({
        id: editingCustomer.id,
        payload: {
          name: formData.name,
          phone: formData.phone || null,
          email: formData.email || null,
          tax_id: formData.tax_id || null,
          legal_name: formData.legal_name || null,
          tax_regime: formData.tax_regime || null,
          zip_code: formData.zip_code || null,
          credit_limit_cents: limitCents,
          credit_days: days,
          notes: formData.notes || null,
        },
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        tax_id: formData.tax_id || undefined,
        legal_name: formData.legal_name || undefined,
        tax_regime: formData.tax_regime || undefined,
        zip_code: formData.zip_code || undefined,
        credit_limit_cents: limitCents,
        credit_days: days,
        notes: formData.notes || undefined,
      });
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-surface p-6">
      {/* Encabezado */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-carbon">
            Directorio de Clientes
          </h1>
          <p className="text-sm text-muted">
            Administración de clientes, historial comercial, datos fiscales y líneas de crédito.
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Buscador */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Buscar por nombre, teléfono, email o RFC..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-msr border border-line bg-snow py-2 pl-9 pr-4 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
          />
        </div>
        <span className="text-sm text-muted">
          {customersData ? `${customersData.total} clientes encontrados` : "Cargando..."}
        </span>
      </div>

      {/* Tabla de clientes */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                <th className="p-4">Cliente</th>
                <th className="p-4">Contacto</th>
                <th className="p-4">Datos Fiscales</th>
                <th className="p-4">Límite Crédito</th>
                <th className="p-4">Saldo Utilizado</th>
                <th className="p-4">Días Crédito</th>
                <th className="p-4">Estado</th>
                <th className="p-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted">
                    Cargando directorio de clientes...
                  </td>
                </tr>
              ) : customersData?.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted">
                    No se encontraron clientes registrados con ese criterio.
                  </td>
                </tr>
              ) : (
                customersData?.items.map((cust) => {
                  const availableCredit = cust.credit_limit_cents - cust.credit_balance_cents;
                  return (
                    <tr key={cust.id} className="hover:bg-surface/30">
                      <td className="p-4">
                        <div className="font-semibold text-carbon">{cust.name}</div>
                        {cust.legal_name && cust.legal_name !== cust.name && (
                          <div className="text-xs text-muted">{cust.legal_name}</div>
                        )}
                      </td>
                      <td className="p-4 text-xs">
                        {cust.phone && <div>📞 {cust.phone}</div>}
                        {cust.email && <div className="text-muted">✉️ {cust.email}</div>}
                        {!cust.phone && !cust.email && <span className="text-muted">Sin contacto</span>}
                      </td>
                      <td className="p-4 text-xs">
                        {cust.tax_id ? (
                          <div>
                            <span className="font-mono font-medium text-carbon">{cust.tax_id}</span>
                            {cust.zip_code && (
                              <span className="text-muted ml-1">CP {cust.zip_code}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Sin RFC</span>
                        )}
                      </td>
                      <td className="p-4">
                        <Money cents={cust.credit_limit_cents} />
                      </td>
                      <td className="p-4">
                        <Money
                          cents={cust.credit_balance_cents}
                          className={cust.credit_balance_cents > 0 ? "font-semibold text-danger" : ""}
                        />
                        {cust.credit_limit_cents > 0 && (
                          <div className="text-[10px] text-muted">
                            Disponible: <Money cents={availableCredit} size="sm" />
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        {cust.credit_days > 0 ? (
                          <span className="text-xs font-medium text-carbon">
                            {cust.credit_days} días
                          </span>
                        ) : (
                          <span className="text-xs text-muted">Contado</span>
                        )}
                      </td>
                      <td className="p-4">
                        <StatusPill tone={cust.is_active ? "ok" : "neutral"}>
                          {cust.is_active ? "Activo" : "Inactivo"}
                        </StatusPill>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedCustomerId(cust.id)}
                            className="rounded p-1 text-muted hover:bg-surface hover:text-carbon"
                            title="Ver Historial"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleOpenEdit(cust)}
                            className="rounded p-1 text-muted hover:bg-surface hover:text-carbon"
                            title="Editar Cliente"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal Crear / Editar Cliente */}
      {(showCreateModal || editingCustomer) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-msr-lg border border-line bg-snow p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between border-b border-line pb-4">
              <h2 className="font-display text-lg font-bold text-carbon">
                {editingCustomer ? "Editar Cliente" : "Alta de Cliente"}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingCustomer(null);
                }}
                className="text-muted hover:text-carbon"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Nombre o Razón Social *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                    placeholder="Ej. Comercializadora del Bajío SA de CV"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                    placeholder="4421234567"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                    placeholder="facturacion@cliente.com"
                  />
                </div>
              </div>

              {/* Sección Fiscal */}
              <div className="rounded-msr border border-line/70 bg-surface/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase text-carbon flex items-center gap-2">
                  <FileText className="h-4 w-4 text-electric" />
                  Datos Fiscales (CFDI 4.0)
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-muted">RFC</label>
                    <input
                      type="text"
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value.toUpperCase() })}
                      className="w-full rounded-msr border border-line px-3 py-1.5 font-mono text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                      placeholder="XAXX010101000"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-muted">
                      Régimen Fiscal
                    </label>
                    <select
                      value={formData.tax_regime}
                      onChange={(e) => setFormData({ ...formData, tax_regime: e.target.value })}
                      className="w-full rounded-msr border border-line px-3 py-1.5 text-sm text-carbon focus:border-electric focus:outline-none"
                    >
                      <option value="601">601 - General de Ley Personas Morales</option>
                      <option value="612">612 - Personas Físicas con Actividades Empresariales</option>
                      <option value="626">626 - Régimen Simplificado de Confianza (RESICO)</option>
                      <option value="605">605 - Sueldos y Salarios</option>
                      <option value="616">616 - Sin obligaciones fiscales</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-muted">Código Postal</label>
                    <input
                      type="text"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      className="w-full rounded-msr border border-line px-3 py-1.5 text-sm text-carbon focus:border-electric focus:outline-none"
                      placeholder="76000"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-muted">
                      Nombre Fiscal Completo
                    </label>
                    <input
                      type="text"
                      value={formData.legal_name}
                      onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                      className="w-full rounded-msr border border-line px-3 py-1.5 text-sm text-carbon focus:border-electric focus:outline-none"
                      placeholder="Sin régimen societario para CFDI 4.0"
                    />
                  </div>
                </div>
              </div>

              {/* Crédito y Condiciones */}
              <div className="rounded-msr border border-line/70 bg-surface/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase text-carbon flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-electric" />
                  Condiciones Comerciales y Crédito
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-muted">
                      Límite de Crédito (MXN)
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={formData.credit_limit_pesos}
                      onChange={(e) => setFormData({ ...formData, credit_limit_pesos: e.target.value })}
                      className="w-full rounded-msr border border-line px-3 py-1.5 text-sm text-carbon focus:border-electric focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-muted">
                      Días de Crédito
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.credit_days}
                      onChange={(e) => setFormData({ ...formData, credit_days: e.target.value })}
                      className="w-full rounded-msr border border-line px-3 py-1.5 text-sm text-carbon focus:border-electric focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">Notas Internas</label>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  placeholder="Referencias, condiciones de entrega o preferencias..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCustomer(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingCustomer ? "Guardar Cambios" : "Crear Cliente"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Drawer / Modal Detalle Cliente con Historial */}
      {selectedCustomerId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-msr-lg border border-line bg-snow p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between border-b border-line pb-4">
              <div>
                <h2 className="font-display text-lg font-bold text-carbon">
                  {detailData?.customer.name}
                </h2>
                <p className="text-xs text-muted">
                  RFC: {detailData?.customer.tax_id ?? "No registrado"} | Tel:{" "}
                  {detailData?.customer.phone ?? "No registrado"}
                </p>
              </div>
              <button
                onClick={() => setSelectedCustomerId(null)}
                className="text-muted hover:text-carbon"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {isLoadingDetail ? (
              <div className="p-8 text-center text-muted">Cargando historial del cliente...</div>
            ) : detailData ? (
              <div className="space-y-6">
                {/* Métricas de crédito */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-msr border border-line bg-surface p-4">
                    <div className="text-xs text-muted">Límite Autorizado</div>
                    <Money
                      cents={detailData.customer.credit_limit_cents}
                      size="lg"
                      className="mt-1"
                    />
                  </div>
                  <div className="rounded-msr border border-line bg-surface p-4">
                    <div className="text-xs text-muted">Saldo Por Pagar</div>
                    <Money
                      cents={detailData.customer.credit_balance_cents}
                      size="lg"
                      className="mt-1 text-danger font-bold"
                    />
                  </div>
                  <div className="rounded-msr border border-line bg-surface p-4">
                    <div className="text-xs text-muted">Crédito Disponible</div>
                    <Money
                      cents={
                        detailData.customer.credit_limit_cents -
                        detailData.customer.credit_balance_cents
                      }
                      size="lg"
                      className="mt-1 text-success font-bold"
                    />
                  </div>
                </div>

                {/* Historial de Compras en POS */}
                <div>
                  <h3 className="mb-3 text-sm font-bold uppercase text-carbon flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-electric" />
                    Últimas Ventas ({detailData.sales.length})
                  </h3>
                  {detailData.sales.length === 0 ? (
                    <p className="text-xs text-muted">No cuenta con compras registradas aún.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-msr border border-line">
                      <table className="w-full text-left text-xs">
                        <thead className="border-b border-line bg-surface/60 font-semibold text-muted">
                          <tr>
                            <th className="p-2">Folio</th>
                            <th className="p-2">Fecha</th>
                            <th className="p-2">Estado</th>
                            <th className="p-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {detailData.sales.map((sale) => (
                            <tr key={sale.id}>
                              <td className="p-2 font-mono font-medium">
                                {sale.series}-{sale.folio}
                              </td>
                              <td className="p-2 text-muted">
                                {new Date(sale.created_at).toLocaleDateString()}
                              </td>
                              <td className="p-2">
                                <StatusPill tone={sale.status === "pagada" ? "ok" : "danger"}>
                                  {sale.status}
                                </StatusPill>
                              </td>
                              <td className="p-2 text-right font-medium">
                                <Money cents={sale.total_cents} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Historial de Órdenes de Servicio / Taller */}
                <div>
                  <h3 className="mb-3 text-sm font-bold uppercase text-carbon flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-electric" />
                    Órdenes de Reparación ({detailData.orders.length})
                  </h3>
                  {detailData.orders.length === 0 ? (
                    <p className="text-xs text-muted">No registra equipos en servicio técnico.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-msr border border-line">
                      <table className="w-full text-left text-xs">
                        <thead className="border-b border-line bg-surface/60 font-semibold text-muted">
                          <tr>
                            <th className="p-2">Folio</th>
                            <th className="p-2">Equipo</th>
                            <th className="p-2">Fecha Ingreso</th>
                            <th className="p-2">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {detailData.orders.map((ord) => (
                            <tr key={ord.id}>
                              <td className="p-2 font-mono font-medium">
                                {ord.series}-{ord.folio}
                              </td>
                              <td className="p-2">
                                {ord.device_type} {ord.brand} {ord.model}
                              </td>
                              <td className="p-2 text-muted">
                                {new Date(ord.received_at).toLocaleDateString()}
                              </td>
                              <td className="p-2">
                                <StatusPill tone="info">{ord.status}</StatusPill>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
